/* eslint-disable max-len */
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import moment from 'moment';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { TimezoneService } from 'src/app/reusables/services/timezone.service';
import { DashboardTimezoneInfo } from 'src/AngularJS/Admin/timezone.model';
import { AgentStateCellRendererComponent } from '../../modules/current-agent-states/agent-state-cell-renderer/agent-state-cell-renderer.component';
import { AgentFrontEnd, AgentStateType, EState, SingleAgentDetails } from '../../modules/current-agent-states/current-agent-states.models';
import { CurrentAgentStatesService } from '../../modules/current-agent-states/current-agent-states.service';
import { ModuleManagerExportFormat } from 'src/shared/models/module-manager/module-manager-options.model';
import { ModuleExportFormat } from 'src/AngularJS/Dashboard/Models/module-export-format.model';
import { AgGridAngular } from 'ag-grid-angular';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { Partners } from 'src/AngularJS/Admin/Company/company.model';
import { ClearViewUtils } from 'src/shared/utils';

@Component({
    selector: 'cv-agent-state-detail',
    templateUrl: './agent-state-detail.component.html',
    styleUrls: ['./agent-state-detail.component.scss']
})
export class AgentStateDetailComponent implements OnInit {
    @ViewChild('agentStateDetailGrid') agGrid: AgGridAngular;
    @Input() selectedRow: any;
    @Input() moduleKey: string;
    @Input() groupID: number;
    @Input() modalOpen: boolean;
    @Input() dashKey: string;

    data = new SingleAgentDetails();
    dateTimeDisplay: string | any = "Current";
    updatedTime: string = null;
    dateFormat = CVAngularDateFormat;
    gridOptions = null;
    isCXOne = false;
    agentStateCategories = { // Is replaced by a successful loadData
        Inbound: "Inbound",
        Outbound: "Outbound",
        Available: "Available",
        Unavailable: "Unavailable",
        ACW: "ACW",
        LoggedIn: "Logged In",
        LoggedOut: "Logged Out",
        Dialer: "Dialer",
    };
    noData = false;
    agentStateCategoriesReady = false;
    selectedDate = new Date(); //No specific value, defaults to today.
    maxDate = new Date();
    isLoading = true;
    firstSet = false;
    today: Date = null;
    mstOffset = 0;
    twoWeeksAgo: Date = null;
    stateSummary = {
        Available: 0,
        Unavailable: 0,
        Inbound: 0,
        Outbound: 0,
        ACW: 0,
        LoggedIn: 0,
        LoggedOut: 0,
        Dialer: 0,
        UnavailableSubStates: [],
        stateSummaryTotal: 0,
    };
    agent = new AgentFrontEnd();
    settings = {
        hybridModeChecked: false,
        useEndDateTime: false,
        endDateTime: null
    };
    colors = {
        green: "#278A33",
        purple: "#7D3BC8",
        yellow: "#EFA716",
        red: "#CC0816",
        blue: "#007CBE",
        orange: "#F05D21",
        cyan: "#00BBBD",
        brown: "#463A32",
        pink: "#E31C79",
        gray: "#D3D6DA",
    };
    exportFormats: { [name: string]: ModuleExportFormat } = {};

    totalLabel = "";
    exportLabel = "";

    // currentAgentStatesModuleFactory.dataObj.hideLoadingScreen();
    constructor(
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private service: CurrentAgentStatesService,
        private activeModal: NgbActiveModal,
        private configService: CvConfigurationService,
        private timezoneService: TimezoneService,
        private navigationService: NavigationService,
    ) {
        this.isCXOne = configService.isCxOne;
        // export formats
        this.exportFormats = {
            csv: {
                description: 'CSV',
                mime: 'text/csv',
                extension: 'csv',
            },
            excel: {
                description: 'Excel',
                mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                extension: 'xlsx',
            },
        };
    }

    async ngOnInit() {
        // --- Things to do before module renders ---
        // I think this was happening in the factory and setting the response selected row to selected row
        // this.selectedRow = currentAgentStatesModuleFactory.dataObj.selectedRow
        this.agent.name = this.selectedRow.GroupName;
        this.agent.currentState = this.selectedRow.CurrentState;
        this.agent.currentState.displayState = this.selectedRow.CurrentState.DisplayState;
        this.agent.duration = this.selectedRow.Duration;
        this.agent.profilePicturePath = '../Admin/ImageResizer.aspx?imgid=-2&w=56&h=56'; //Default to a generic profile image
        this.dateTimeDisplay = "Current";
        await this.prepareDates();
        this.agent.stateStartTime = moment(this.selectedRow.StateStartTime).add(this.mstOffset, 'h').format(CVMomentTimeFormat);
        this.cvLocalizationService.GetSection('Agent_State_Details').finally(() => this.setupGrid());
        //Set the values in a $timeout, forcing values to update after element is rendered in the DOM.
        //  This allows a "No Value to Actual Value" transition to occur, for visual fun.
        setTimeout(() => {
            //Call loadData
            this.loadData();
        }, 500);
        this.totalLabel = "(" + this.cvLocalizationService.getCachedText("Agent_State_Details", "Total", "Total") + ")";
        this.exportLabel = this.cvLocalizationService.getCachedText("Agent_State_Details", "Export", "Export");
    }

    async prepareDates() {
        // retrieving timezone set in dashboard or user or company, if all are null offsetSeconds will default to the companys MSTOffset
        const tzi = await this.timezoneService.getDashUserCompTimezone(this.dashKey, this.moduleKey) as DashboardTimezoneInfo;
        // removing browser timezone
        const now = new Date();
        const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
        // adding back in the dashboard timezone and setting max and min dates selectable
        this.today = new Date(utc).addSeconds(Number(tzi.TimezoneOffsetSeconds));

        this.mstOffset = tzi.MSTOffset;
        // This is initializing the selected date. The last three lines are setting the max date ranges that can be selected.
        this.selectedDate = new Date(this.today);
        this.maxDate = new Date(this.today);
        this.today.setHours(23, 59, 59, 999);
        this.twoWeeksAgo = new Date(this.today).addDays(-13);
        this.twoWeeksAgo.setHours(0, 0, 0, 0);
    }


    setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            defaultColDef: {
                sortable: this.configService.isCxOne ? true : false,
                suppressMenu: true,
                icons: this.configService.isCxOne ? {
                    sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                    filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                    menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                    columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
                }: null,
            },
            suppressContextMenu: true,
            suppressMovableColumns: true,
            headerHeight: this.configService.isCxOne ? 44 : null,
            rowHeight: this.configService.isCxOne ? 36 : null,
            rowClass: this.configService.isCxOne ? 'cx-one-ui' : null,
            components: {
                AgentStateCellRenderer: AgentStateCellRendererComponent,
            },
            overlayNoRowsTemplate: this.configService.isCxOne ? `<div class="error-container">
                                        <svg class="error-icon">
                                            <use href="../App_Themes/CXone/icons/empty.svg#empty"></use>
                                        </svg>
                                        <p class="cxone-error-text">${this.cvLocalizationService.getCachedText("Agent_State_Details", "No_Data_Available", "No data available")}</p>
                                    </div>` : null,
            columnDefs: [
                {
                    field: "AgentStateName",
                    colId: "AgentStateName",
                    cellRenderer: 'AgentStateCellRenderer',
                    // this cell renderer is used in both agent state detail and the main table. this function below is used in the table
                    // and so must be included here as well to not break the cell renderer.
                    cellRendererParams: {
                        getColorMap: () => false
                    },
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Header_State", "State"),
                    width: this.configService.isCxOne ? 230 : null,
                    minWidth: this.configService.isCxOne ? 230 : null,
                },
                {
                    field: "startTimeDisplayValue",
                    colId: "startTimeDisplayValue",
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Header_Start_Time", "Start Time"),
                    width: this.configService.isCxOne ? 150 : null,
                    minWidth: this.configService.isCxOne ? 150 : null,
                    valueFormatter: params => moment(params.data.StartDate).format('h:mm A').toString(), /* hour : minute (two digit) : second (two digit) AM/PM */
                },
                {
                    field: "durationDisplayValue",
                    colId: "durationDisplayValue",
                    headerName: this.configService.isCxOne ? this.cvLocalizationService.getCachedText("Agent_State_Details", "CXone_Duration", "Duration") : this.cvLocalizationService.getCachedText("Agent_State_Details", "Header_Duration", "Duration"),
                    width: this.configService.isCxOne ? 150 : null,
                    minWidth: this.configService.isCxOne ? 150 : null,
                },
                {
                    field: "SkillName",
                    colId: "SkillName",
                    headerName: this.cvLocalizationService.getCachedText("Agent_State_Details", "Header_Skill", "Skill"),
                    width: this.configService.isCxOne ? 200 : null,
                    minWidth: this.configService.isCxOne ? 200 : null,
                }
            ],
            onGridReady: () => {
                if (this.data.Details)
                    this.agGrid?.api.setGridOption('rowData', this.data.Details);
            },
        });
    }

    getFormattedDate() {
        //Endpoint expects date in format: Year, Month, Date
        //return ''.concat(ctrl.selectedDate.getFullYear(), '-', ctrl.selectedDate.getMonth()+1, '-', ctrl.selectedDate.getDate());
        if (this.dateTimeDisplay === "Current") {
            const currentDate = new Date(this.today);
            return currentDate.getFullYear() + '-' + (currentDate.getMonth() + 1) + '-' + currentDate.getDate();
        } else {
            return this.dateTimeDisplay.getFullYear() + '-' + (this.dateTimeDisplay.getMonth() + 1) + '-' + this.dateTimeDisplay.getDate();
        }
    };

    datePickerModel() {
        const today = new Date();
        return today.getMonth() + "/" + today.getDate() + "/" + today.getFullYear();
    };

    public async exportData(format: string) {
        const response = await this.service.getDataForExport(this.moduleKey, format, this.groupID, "", "", this.selectedRow.GroupName, this.getFormattedDate(), false);
        this.activeModal.dismiss();
        return response;
    }

    async exportClicked(format: string | ModuleManagerExportFormat) {
        if (typeof this.exportData === 'function') {
            const formatDetails = this.exportFormats[format];
            const fileName = "Current Agent States" + " " + formatDetails.description + "_" + (new Date()).toString('yyyymmddhhmmss');

            const result = await this.exportData(format as ModuleManagerExportFormat);
            if (result != null) {
                this.service.downloadFile(result.data, formatDetails.mime, fileName + '.' + formatDetails.extension);
            }
        }
    };

    getColorForAgentStateType(ast) { //agentStateType: ast
        switch (ast) {
            case AgentStateType.Inbound:
                return this.colors.purple;
            case AgentStateType.Outbound:
                return this.colors.yellow;
            case AgentStateType.Available:
                return this.colors.green;
            case AgentStateType.Unavailable:
            default:
                return this.colors.red;
        }
    }

    agentStateTypeIntToEnum(ast) { //agentStateType: ast
        // When serialized, the enum keys of the AgentStateCategories string are converted to the string values of those enums.
        // This function goes from int to enum key value (doing this for time)
        switch (ast) {
            case AgentStateType.Inbound:
                return "Inbound";
            case AgentStateType.Outbound:
                return "Outbound";
            case AgentStateType.Available:
                return "Available";
            case AgentStateType.Unavailable:
            default:
                return "Unavailable";
        }
    }

    getStateColor(state) {
        const unknownState = this.cvLocalizationService.getCachedText("Current_Agent_States_Module", "Unknown", "Unknown");
        if (state && state.State !== undefined) {
            if (state.IsACW) {
                return this.colors.orange;
            }
            if (state.IsLoggedOut) {
                return this.colors.gray;
            }
            if (state.IsDialer) {
                return this.colors.blue;
            }
            if (state.DisplayState === unknownState) {
                return this.colors.gray;
            }
            switch (state.State) { // "state" should be of type CurrentAgentStates.AgentState in the back-end.  "state.State" should be the enum value.
                case 1: // Available
                    return this.colors.green;
                case 2: // Inbound
                    return this.colors.purple;
                case 4: // Outbound
                    return this.colors.yellow;
                case 8: // Unavailable
                case 16: // Custom
                default: // Maybe: Mixed, Unknown
                    return this.colors.red;
            }
        } else return this.colors.red;
    }

    setStateSummary() {
        this.data.Details.forEach(state => {
            switch (state.AgentStateType) {
                case AgentStateType.Inbound:
                    this.stateSummary.Inbound += state.Duration;
                    break;
                case AgentStateType.Outbound:
                    this.stateSummary.Outbound += state.Duration;
                    break;
                case AgentStateType.Available:
                    this.stateSummary.Available += state.Duration;
                    break;
                case AgentStateType.ACW:
                    this.stateSummary.ACW += state.Duration;
                    break;
                case AgentStateType.LoggedIn:
                    this.stateSummary.LoggedIn += state.Duration;
                    break;
                case AgentStateType.LoggedOut:
                    this.stateSummary.LoggedOut += state.Duration;
                    break;
                case AgentStateType.Dialer:
                    this.stateSummary.Dialer += state.Duration;
                    break;
                case AgentStateType.Unavailable:
                default:
                    this.subUnavailableSubStateSummary(state); // this function will try to decide if the value needs to be added to ctrl.stateSummary.UnavailableSubStates
                    this.stateSummary.Unavailable += state.Duration;
                    break;
            }
        });

        // Convert the substates (an associative array) to an iterable array
        // The non-standard substates array is initially set up as associative (collection[key]=value) to make creating and summing values cleaner and quicker.
        // But to iterate over these states on the front end, we need something a bit more iterable. (An associative array will have a length of zero.)
        const newSubStateCollection = [];
        let unavailableSubStateTotal = 0;
        Object.keys(this.stateSummary.UnavailableSubStates).forEach(value => {
            newSubStateCollection.push(this.stateSummary.UnavailableSubStates[value]);
            unavailableSubStateTotal += this.stateSummary.UnavailableSubStates[value].summary;
        });
        this.stateSummary.UnavailableSubStates = newSubStateCollection;

        // Set total for reuse in other functions!
        this.stateSummary.stateSummaryTotal = this.stateSummary.Available +
            this.stateSummary.Inbound +
            this.stateSummary.Outbound +
            this.stateSummary.ACW +
            this.stateSummary.LoggedIn +
            this.stateSummary.LoggedOut +
            this.stateSummary.Dialer +
            this.stateSummary.Unavailable;
    }

    subUnavailableSubStateSummary(state) {
        if (state.AgentStateType === EState.Unavailable /*unavailable*/ && state.displayStatus === this.agentStateCategories.Unavailable)
            return;

        if (this.stateSummary.UnavailableSubStates[state.displayStatus] === undefined) {
            this.stateSummary.UnavailableSubStates[state.displayStatus] = {
                name: state.displayStatus,
                summary: 0
            };
        }

        // Add the duration to the substate.
        this.stateSummary.UnavailableSubStates[state.displayStatus].summary += state.Duration;
    }

    getStateSummaryPercentage(key) {
        if (this.stateSummary[key] === undefined || this.stateSummary[key] === 0)
            return '0%';
        else {
            let percentage = parseFloat((100 * (this.stateSummary[key] / this.stateSummary.stateSummaryTotal)).toFixed(2));
            if (percentage > 0 && percentage < 0.5) {
                percentage = 0.5;
            }
            return percentage + '%';
        }
    }

    getSubStateSummaryPercentage(index) {
        if (this.stateSummary.UnavailableSubStates[index].summary === undefined ||
            this.stateSummary.UnavailableSubStates[index].summary === 0) {
            return '0%';
        } else {
            return (100 * (this.stateSummary.UnavailableSubStates[index].summary / this.stateSummary.stateSummaryTotal)).toFixed(2) + '%';
        }
    }

    getStateSummaryFormatted(key) {
        if (this.stateSummary[key] === undefined || this.stateSummary[key] === 0)
            return '00:00:00';
        else
            return moment('2018-01-01').startOf('day').seconds(this.stateSummary[key]).format('HH:mm:ss').toString();
    }

    getSubStateSummaryFormatted(index) {
        if (this.stateSummary.UnavailableSubStates[index].summary === undefined ||
            this.stateSummary.UnavailableSubStates[index].summary === 0) {
            return '00:00:00';
        } else {
            return moment('2018-01-01')
                .startOf('day')
                .seconds(this.stateSummary.UnavailableSubStates[index].summary)
                .format('HH:mm:ss')
                .toString();
        }
    }

    // need to convert to miliseconds for sorting to work in agGrid correctly
    getMiliFromDateTime(timestamp) {
        return new Date(timestamp).getTime();
    }

    convertTimeFromSeconds(seconds) {
        return moment('2018-01-01').startOf('day').seconds(seconds).format('HH:mm:ss').toString();
    }

    close() {
        this.activeModal.close();
    }

    // Render values after element exists in DOM
    setInitialStateSummaries() {
        this.stateSummary = {
            Available: 0,
            Unavailable: 0,
            Inbound: 0,
            Outbound: 0,
            ACW: 0,
            LoggedIn: 0,
            LoggedOut: 0,
            Dialer: 0,
            UnavailableSubStates: [],
            stateSummaryTotal: 0,
        };

        this.firstSet = true;
    }

    async loadData() {
        this.isLoading = true;

        this.data = await this.service.getSingleAgentStates(this.moduleKey, this.groupID, "", "", this.getFormattedDate(), true);
        if (this.data.AgentStateCategories) {
            Object.keys(this.data.AgentStateCategories).forEach(agentStateType => {
                const standardizedKey = ClearViewUtils.standardizeLocalizationKey(this.data.AgentStateCategories[agentStateType]);
                this.agentStateCategories[agentStateType] = this.cvLocalizationService.getCachedText("Agent_State_Details", standardizedKey, this.data.AgentStateCategories[agentStateType]);
            });
            this.agentStateCategories["ACW"] = this.cvLocalizationService.getCachedText("Agent_State_Details", "ACW", "ACW");
            this.agentStateCategories["LoggedIn"] = this.cvLocalizationService.getCachedText("Agent_State_Details", "Logged_In", "Logged In");
            this.agentStateCategories["LoggedOut"] = this.cvLocalizationService.getCachedText("Agent_State_Details", "Logged_Out", "Logged Out");
            this.agentStateCategories["Dialer"] = this.cvLocalizationService.getCachedText("Agent_State_Details", "Dialer", "Dialer");
        }

        this.agent.profilePicturePath = this.data.ImagePath;

        // Set colors, state display value and Duration column text
        // NOTE: The current agent state (shown at the very top of the modal) is NOT set in this.
        // It's set in the ctrl scope (where things like ctrl.loadData are defined), as the values for that were already passed in.
        this.data.Details.forEach(state => {
            // Display status
            const enumString = this.agentStateTypeIntToEnum(state.AgentStateType);
            // One of these will provide a value...
            if (state.OutStateDescription) {
                state.displayStatus = state.OutStateDescription;
            } else if (!state.OutStateDescription && state.AgentStateName) {
                state.displayStatus = state.AgentStateName;
            } else if (!state.OutStateDescription && this.agentStateCategories[enumString]) {
                state.displayStatus = this.agentStateCategories[enumString];
            } else if (!state.OutStateDescription && enumString) {
                state.displayStatus = enumString;
            }

            // Display Color
            // If isACW = true, use orange, regardless of state.
            // If IsLoggedIn = true, use blue, regardless of state.
            // If IsLoggedOut = true, use gray, regardless of state.
            // If isDialer = true, use blue, regardless of state.
            // Otherwise, set color based on the state name.

            if (state.IsACW) {
                state.AgentStateType = AgentStateType.ACW;
                state.displayColor = this.colors.orange;
            } else if (state.IsLoggedIn) {
                state.AgentStateType = AgentStateType.LoggedIn;
                state.displayColor = this.colors.blue;
            } else if (state.IsLoggedOut) {
                state.AgentStateType = AgentStateType.LoggedOut;
                state.displayColor = this.colors.gray;
            } else if (state.IsDialer) {
                state.AgentStateType = AgentStateType.Dialer;
                state.displayColor = this.colors.blue;
            } else {
                state.displayColor = this.getColorForAgentStateType(state.AgentStateType);
            }

            // Convert and set time display values
            // Convert once, reuse.
            state.startTimeDisplayValue = this.getMiliFromDateTime(state.StartDate);
            state.durationDisplayValue = this.convertTimeFromSeconds(state.Duration);

            // Check if split across multiple days (StartDate == 12:00:00 AM or EndDate == 11:59:59 PM)
            // Raw UTC times are given from the DB. Split on the T (literally!) and check the right half.
            state.spansMultipleDays = (state.EndDate.split('T')[1] === "11:59:59" || state.StartDate.split('T')[1] === "00:00:00");

            // Localizing state names.
            const standardizedKey = ClearViewUtils.standardizeLocalizationKey(state.displayStatus);
            state.displayStatus = this.cvLocalizationService.getCachedText("Agent_State_Details", standardizedKey, state.displayStatus);
        });

        // Set summary values
        this.setInitialStateSummaries(); // Reset the state summaries. Specifically to handle data refreshes and dataset changes.
        this.setStateSummary();

        if (this.gridOptions != null && this.agGrid?.api != null) {
            this.agGrid?.api.setGridOption('rowData', this.data.Details);
        }

        this.updatedTime = this.data.LastUpdatedDateTime;
        this.isLoading = false;
        this.agentStateCategoriesReady = true;
        this.noData = this.data.Details.length === 0;
    }

    onCustomDateRangeChanged() {
        this.settings.useEndDateTime = true;
        if (this.selectedDate === null) this.settings.endDateTime = new Date(this.today);
        else this.settings.endDateTime = this.selectedDate;
        this.updateDateTimeUI();
    }

    updateDateTimeUI() {
        if (this.settings.useEndDateTime) {
            if (this.settings.endDateTime === null)
                this.settings.endDateTime = new Date(this.today);

            this.dateTimeDisplay = this.settings.endDateTime;
            this.loadData();
        }
        else {
            this.dateTimeDisplay = "Current";
        }
    }

    onDatePickerChanged() {
        this.dateTimeDisplay = this.selectedDate;
        this.loadData();
    }

    isCurrentDate() {
        return (this.dateTimeDisplay === "Current");
    }

    /**
     * Determines if the Dialer section should be included based on the company partner.
     * @returns True if the chart should include the Dialer section, false otherwise.
     */
    shouldIncludeDialer(): boolean {
        const excludedPartners = [Partners.Five9, Partners.UJet];
        return !excludedPartners.includes(this.navigationService.companyPartner);
    }
}
